/**
 * RedactionThinkingStream — reasoning du modèle streamé en direct pendant
 * l'insertion du plan/acte dans le canvas.
 *
 * Port web de `apps/addin-word/src/taskpane/components/generation/shared/ThinkingStreamDisplay.tsx`
 * (validé produit côté Word). Différences vs add-in : palette stone-* (vs
 * gray-*), classes CSS préfixées `redaction-` pour éviter toute collision
 * globale (le style est inline, non scopé par CSS modules).
 *
 * Comportement (parité stricte) :
 *  - auto-expand quand le reasoning démarre, auto-collapse 600 ms après le
 *    premier token de contenu (le raisonnement reste consultable au clic) ;
 *  - fenêtre 120 px max auto-scrollée vers le bas à chaque delta ;
 *  - titre « Raisonnement en cours » avec shimmer tant que le modèle réfléchit.
 */

import * as React from 'react';
import { ChevronUp } from 'lucide-react';

export interface RedactionThinkingStreamProps {
  /** Reasoning accumulé du modèle (append-only pendant le stream). */
  content: string;
  /** True tant que le modèle réfléchit (aucun token de contenu émis). */
  isThinking: boolean;
}

export function RedactionThinkingStream({ content, isThinking }: RedactionThinkingStreamProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [wasThinking, setWasThinking] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Auto-expand quand le reasoning démarre
  React.useEffect(() => {
    if (isThinking && content) {
      setIsExpanded(true);
    }
  }, [isThinking, content]);

  // Auto-collapse quand le reasoning se termine (premiers tokens de contenu)
  React.useEffect(() => {
    if (wasThinking && !isThinking && content) {
      const timer = setTimeout(() => setIsExpanded(false), 600);
      return () => clearTimeout(timer);
    }
    setWasThinking(isThinking);
    return undefined;
  }, [isThinking, content, wasThinking]);

  // Auto-scroll vers le bas à chaque nouveau delta
  React.useEffect(() => {
    if (isExpanded && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [content, isExpanded]);

  if (!content) return null;

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-500 transition-colors"
      >
        {isThinking ? (
          <span className="redaction-thinking-shimmer">Raisonnement en cours</span>
        ) : (
          <span>Raisonnement</span>
        )}
        <ChevronUp
          className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? '' : 'rotate-180'}`}
        />
      </button>

      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ${
          isExpanded ? 'max-h-[120px] opacity-100 mt-1.5' : 'max-h-0 opacity-0'
        }`}
      >
        <div
          ref={contentRef}
          className="border-l border-stone-200 pl-2.5 max-h-[120px] overflow-y-auto redaction-thinking-scrollbar"
        >
          <p className="text-[11px] text-stone-400 whitespace-pre-wrap leading-relaxed">
            {content}
          </p>
        </div>
      </div>

      {/* Shimmer + scrollbar (scopés par préfixe de classe) */}
      <style>{`
        .redaction-thinking-shimmer {
          background: linear-gradient(90deg, #a8a29e 0%, #78716c 50%, #a8a29e 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: redaction-thinking-shimmer 2s linear infinite;
        }
        @keyframes redaction-thinking-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .redaction-thinking-scrollbar::-webkit-scrollbar { width: 3px; }
        .redaction-thinking-scrollbar::-webkit-scrollbar-thumb { background: #d6d3d1; border-radius: 2px; }
        .redaction-thinking-scrollbar::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}
